import React, { useState, useRef, useEffect } from 'react';
import cn from 'classnames';
import { Button, SvgIcon, TooltipDisclaimer } from '@ui-elements';
import { CompareOtherModelProps, CompareRangeModel } from './store';
import styles from './index.module.scss';
import { CompareRangeHeader } from '~/pages/model-landing/components/model-landing-compare-header';
import { CompareRangeVariantTab } from '~/pages/model-landing/components/model-landing-campare-variant-tab';
import {
  CompareRangePopupModel,
  CompareRangePopupModelState,
  CompareRangeVariant,
} from '~/pages/model-landing/store';
import ImgLazyLoad from '~/common/components/image-lazy-loading';
import { CompareRangeModal } from '~/pages/model-landing/components/model-landing-compare-modal';
import { usePortals, PortalProvider } from 'react-portal-hook';
import { RecoilRoot } from 'recoil';

export const CompareOtherModels = React.memo(
  ({
    compareRangePopupModels,
    ...props
  }: CompareOtherModelProps & {
    compareRangePopupModels: CompareRangePopupModel[];
  }) => {
    return (
      <RecoilRoot
        initializeState={({ set }) => {
          set(
            CompareRangePopupModelState,
            (compareRangePopupModels ?? []).map((m) => ({
              ...m,
              _active: false,
              variants:
                m.variants?.map((v) => ({
                  ...v,
                  _order: Number.MAX_SAFE_INTEGER,
                  _selected: false,
                })) ?? [],
            }))
          );
        }}
      >
        <PortalProvider>
          <CompareOtherModelsContent {...props} />
        </PortalProvider>
      </RecoilRoot>
    );
  }
);

const CompareOtherModelsContent = (props: CompareOtherModelProps) => {
  const [showAllKF, setShowAllKF] = useState<boolean>(false);
  const [activeVariant, setActiveVariant] = useState<
    CompareRangeVariant | undefined
  >(undefined);

  const [selectedModel, setSelectedModel] = useState<
    CompareRangeModel | undefined
  >(undefined);

  const portalManager = usePortals();
  const parentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!!selectedModel) {
      const variants = selectedModel.modelVariants || [];
      if (variants.length > 0) {
        const variant =
          variants.find((x) => x.compareOtherSpotlight) || variants[0];
        setActiveVariant(variant);
      }
    }
  }, [selectedModel]);

  const showCompareModal = () => {
    portalManager.open(
      (portal) => <CompareRangeModal closeModal={portal.close} />,
      parentRef.current ? { appendTo: parentRef.current } : {}
    );
  };

  const renderCompareCTA = () => {
    return (
      <div className={styles.compareCTA}>
        <Button
          type="primary"
          text="Compare the Subaru range"
          onClick={showCompareModal}
        />
      </div>
    );
  };

  return (
    <div className={cn('full-device-width', styles.compareContainer)}>
      <div>
        <CompareRangeHeader
          headingTag="h2"
          heading={props?.heading}
          subHeading={props?.subHeading}
        />

        <CompareRangeVariantTab
          section="range"
          className={styles.compareRangeVariantTab}
          tabItems={
            props?.models?.map((x) => ({
              id: x.modelId,
              name: x.modelName,
              imageSrc: x.modelImageSrc,
              selected: false,
            })) ?? []
          }
          onChange={(item) => {
            setSelectedModel(
              props?.models?.find((x) => x.modelId === item?.id)
            );
          }}
        />

        <div className={styles.variantContentWrapper}>
          <div>
            <div className={styles.imageWrapper}>
              <ImgLazyLoad
                src={activeVariant?.imageSrc || ''}
                alt={activeVariant?.name}
                loading="lazy"
              />
              {!!activeVariant?.compareOtherImageDisclaimer && (
                <div
                  className={styles.compareOtherImageDisclaimer}
                  dangerouslySetInnerHTML={{
                    __html: activeVariant.compareOtherImageDisclaimer,
                  }}
                />
              )}
            </div>
          </div>
          <div>
            <div className={styles.featureWrapper}>
              <ImgLazyLoad
                className={styles.modelBadge}
                src={selectedModel?.modelBadgeUrl || ''}
                alt={selectedModel?.modelName}
                loading="lazy"
              />

              {activeVariant && (
                <div className={styles.keyFeatures}>
                  <h3>Key features</h3>
                  <div
                    className={cn({
                      [styles.showAllKF]: showAllKF,
                      [styles.useGrid]:
                        activeVariant?.highlightKeyFeatures &&
                        activeVariant?.highlightKeyFeatures.length > 3,
                    })}
                  >
                    {activeVariant?.highlightKeyFeatures?.map(
                      (feature, index) => (
                        <div key={index} className={styles.keyFeatureItem}>
                          <div>
                            <SvgIcon type="check" size={0.8} color="#FFFFFF" />
                          </div>
                          <TooltipDisclaimer
                            followCursor={false}
                            contentClassName={styles.descriptionText}
                            disclaimer={feature.disclaimer}
                          >
                            <span
                              dangerouslySetInnerHTML={{
                                __html: feature.description,
                              }}
                            />
                          </TooltipDisclaimer>
                        </div>
                      )
                    )}

                    {activeVariant?.highlightKeyFeatures &&
                      activeVariant?.highlightKeyFeatures.length > 3 && (
                        <div className={styles.viewAllKeyFeatures}>
                          <a
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              setShowAllKF(!showAllKF);
                            }}
                          >
                            {!showAllKF ? 'View more' : 'Show less'}
                          </a>
                        </div>
                      )}
                  </div>

                  <div className={styles.variantIntroText}>
                    <p
                      dangerouslySetInnerHTML={{
                        __html: activeVariant?.introText as string,
                      }}
                    />
                  </div>
                </div>
              )}
              {!activeVariant?.hideCompareSubaruRangeCta && renderCompareCTA()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
